import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Manual scroll restoration. We can't rely on the browser's native
// scrollRestoration because below-fold sections are lazy-loaded — at
// reload time the document is much shorter than when the user scrolled,
// so the native restore clamps to a tiny scrollHeight and the user
// lands near the top. We save scrollY to sessionStorage on every
// pagehide and restore it after mount with retries while content height
// continues to grow.
const SCROLL_KEY_PREFIX = "onepractix:scrollY:";
const scrollKey = (pathname: string) => `${SCROLL_KEY_PREFIX}${pathname}`;

export function ScrollToTop() {
  const { pathname, hash, state } = useLocation();
  const prevPathname = useRef(pathname);
  const didMount = useRef(false);

  // Disable native scroll restoration — it runs once before lazy chunks
  // mount, so it clamps to a too-small scrollHeight. We restore manually
  // below with retries.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      try {
        window.history.scrollRestoration = "manual";
      } catch {
        /* noop */
      }
    }

    const save = () => {
      try {
        sessionStorage.setItem(
          scrollKey(window.location.pathname),
          String(window.scrollY),
        );
      } catch {
        /* noop */
      }
    };
    // pagehide fires reliably on reload + back/forward on iOS Safari,
    // unlike beforeunload. Also save on visibilitychange as a fallback.
    window.addEventListener("pagehide", save);
    const onVis = () => {
      if (document.visibilityState === "hidden") save();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("pagehide", save);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  useEffect(() => {
    const isInitialMount = !didMount.current;
    didMount.current = true;

    if (hash) {
      // Try immediately, then retry with increasing delays for lazy-loaded content
      const tryScroll = (attempt = 0) => {
        const element = document.getElementById(hash.slice(1));
        if (element) {
          // Account for sticky header height
          const headerHeight = document.querySelector("header")?.getBoundingClientRect().height ?? 80;
          const top = element.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
          window.scrollTo({ top, behavior: "smooth" });
          return;
        }
        // Retry up to 5 times for lazy-loaded sections
        if (attempt < 5) {
          setTimeout(() => tryScroll(attempt + 1), 150);
        }
      };

      // If same page, scroll immediately; if different page, wait for render
      if (prevPathname.current === pathname) {
        tryScroll();
      } else {
        // Wait for the new page to mount
        requestAnimationFrame(() => {
          setTimeout(() => tryScroll(), 50);
        });
      }
    } else {
      const preserveY = (state as { preserveScrollY?: number } | null)?.preserveScrollY;

      // Helper: restore to a target Y, retrying while the document is
      // still growing (lazy chunks mounting). Bails after ~2s.
      const restoreTo = (targetY: number) => {
        const start = performance.now();
        // Stop restoring the moment the visitor scrolls, taps or uses the
        // keyboard; otherwise the retry loop yanks them back to the old Y.
        let cancelled = false;
        const cancel = () => {
          cancelled = true;
          userEvents.forEach((ev) => window.removeEventListener(ev, cancel));
        };
        const userEvents = ["wheel", "touchstart", "keydown", "pointerdown"] as const;
        userEvents.forEach((ev) => window.addEventListener(ev, cancel, { passive: true, once: true }));
        setTimeout(cancel, 2500);
        const tick = () => {
          if (cancelled) return;
          const max = Math.max(
            0,
            document.documentElement.scrollHeight - window.innerHeight,
          );
          const target = Math.min(targetY, max);
          if (Math.abs(window.scrollY - target) > 1) {
            window.scrollTo({ top: target, left: 0, behavior: "instant" });
          }
          // Keep retrying while we haven't reached the requested Y AND
          // page height is still smaller than needed.
          if (max < targetY && performance.now() - start < 2000) {
            setTimeout(tick, 80);
          }
        };
        requestAnimationFrame(tick);
      };

      if (typeof preserveY === "number") {
        // Language switch / explicit preserveScrollY navigation.
        restoreTo(preserveY);
        try {
          window.history.replaceState(
            { ...(window.history.state ?? {}), usr: null },
            "",
          );
        } catch {
          /* noop */
        }
      } else if (isInitialMount) {
        // Initial mount = page load / reload. Restore previously saved
        // scrollY for this pathname, if any.
        let savedY = 0;
        try {
          const raw = sessionStorage.getItem(scrollKey(pathname));
          savedY = raw ? parseInt(raw, 10) || 0 : 0;
        } catch {
          savedY = 0;
        }
        if (savedY > 0) restoreTo(savedY);
      } else {
        // Normal route navigation → top.
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      }
    }

    prevPathname.current = pathname;
  }, [pathname, hash, state]);

  return null;
}
